import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCartStore } from "@/store/cartStore";
import { Button } from "./ui/button";
import { formatKes, cn } from "@/lib/utils";

export function CartDrawer() {
  const isOpen = useCartStore((s) => s.isDrawerOpen);
  const closeDrawer = useCartStore((s) => s.closeDrawer);
  const items = useCartStore((s) => s.items);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const subtotal = useCartStore((s) => s.subtotal());

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-steel/60 backdrop-blur-sm transition-opacity",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={closeDrawer}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        className={cn(
          "fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-plate transition-transform duration-300",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex items-center justify-between border-b border-border p-5">
          <h2 className="font-display text-lg font-semibold text-steel">Your Cart</h2>
          <button onClick={closeDrawer} aria-label="Close cart">
            <X className="h-6 w-6 text-steel" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <p className="text-steel-600">Your cart is empty.</p>
            <Button variant="amber" asChild onClick={closeDrawer}>
              <Link to="/shop">Start Shopping</Link>
            </Button>
          </div>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto divide-y divide-border px-5">
              {items.map((item) => (
                <li key={item.productId} className="flex gap-4 py-4">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" className="h-20 w-20 rounded-sm object-cover" />
                  ) : (
                    <div className="h-20 w-20 rounded-sm bg-concrete-100" />
                  )}
                  <div className="flex flex-1 flex-col">
                    <p className="text-sm font-medium text-steel line-clamp-2">{item.name}</p>
                    <p className="mt-0.5 font-mono text-sm text-steel-600">{formatKes(item.price)}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center gap-2 rounded-sm border border-border">
                        <button
                          onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                          aria-label={`Decrease quantity of ${item.name}`}
                          className="flex h-7 w-7 items-center justify-center"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center font-mono text-sm" aria-live="polite">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                          disabled={item.quantity >= item.stockQuantity}
                          aria-label={`Increase quantity of ${item.name}`}
                          className="flex h-7 w-7 items-center justify-center disabled:opacity-40"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.productId)}
                        aria-label={`Remove ${item.name} from cart`}
                        className="text-steel-600/60 hover:text-rust"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="border-t border-border p-5">
              <div className="mb-4 flex items-center justify-between">
                <span className="font-display text-sm uppercase tracking-wide text-steel-600">Subtotal</span>
                <span className="font-mono text-lg font-semibold text-steel">{formatKes(subtotal)}</span>
              </div>
              <p className="mb-4 text-xs text-steel-600">VAT and shipping calculated at checkout.</p>
              <Button asChild size="lg" className="w-full" onClick={closeDrawer}>
                <Link to="/checkout">Proceed to Checkout</Link>
              </Button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
